import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { Progress } from "@/components/ui/progress";
import { Users, Video, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function BatchesPage() {
  const [batches, setBatches] = useState([]);
  const [courses, setCourses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name:"", course_id:"", type:"Morning", mode:"Offline", capacity:60, timing:"", live_class_url:"" });
  const [liveOpen, setLiveOpen] = useState(null); // holds batch obj for URL edit
  const [urlDraft, setUrlDraft] = useState("");

  const load = async () => {
    const [{data:b},{data:c}] = await Promise.all([api.get("/batches"), api.get("/courses")]);
    setBatches(b); setCourses(c);
  };
  useEffect(()=>{load()},[]);

  const create = async (e) => {
    e.preventDefault();
    const course = courses.find(c => c.id === form.course_id);
    await api.post("/batches", {
      ...form, capacity: Number(form.capacity), enrolled: 0,
      course_name: course?.name, start_date: new Date().toISOString().slice(0,10),
    });
    setOpen(false); toast.success("Batch created"); load();
  };

  const saveLiveUrl = async () => {
    if (!liveOpen) return;
    await api.put(`/batches/${liveOpen.id}`, { ...liveOpen, live_class_url: urlDraft });
    toast.success("Live class URL saved");
    setLiveOpen(null); setUrlDraft("");
    load();
  };

  return (
    <div>
      <PageHeader
        title="Batches"
        subtitle={`${batches.length} batches running`}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><button className="btn-primary" data-testid="add-batch-btn">+ New batch</button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create batch</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-3">
                <input required placeholder="Batch name" data-testid="batch-name-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/>
                <select required data-testid="batch-course-select" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md bg-white" value={form.course_id} onChange={e=>setForm({...form, course_id:e.target.value})}>
                  <option value="">Select course</option>
                  {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <div className="grid grid-cols-2 gap-2">
                  <select data-testid="batch-type-select" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md bg-white" value={form.type} onChange={e=>setForm({...form, type:e.target.value})}>
                    {["Morning","Evening","Weekend"].map(x=><option key={x}>{x}</option>)}
                  </select>
                  <select data-testid="batch-mode-select" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md bg-white" value={form.mode} onChange={e=>setForm({...form, mode:e.target.value})}>
                    {["Offline","Online","Hybrid"].map(x=><option key={x}>{x}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input type="number" data-testid="batch-capacity-input" placeholder="Capacity" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.capacity} onChange={e=>setForm({...form, capacity:e.target.value})}/>
                  <input placeholder="Timing e.g. 07:00-10:00" data-testid="batch-timing-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.timing} onChange={e=>setForm({...form, timing:e.target.value})}/>
                </div>
                <DialogFooter><button className="btn-primary" data-testid="batch-save-btn">Save</button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <DataTable
        testid="batches-table"
        columns={[
          { key: "name", label: "Batch" },
          { key: "course_name", label: "Course" },
          { key: "type", label: "Type" },
          { key: "mode", label: "Mode" },
          { key: "timing", label: "Timing" },
          { key: "occupancy", label: "Occupancy", render: r => {
            const pct = Math.round(((r.enrolled||0) / (r.capacity||1)) * 100);
            return (
              <div className="min-w-[140px]">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-[#00000099] inline-flex items-center gap-1"><Users size={12}/>{r.enrolled||0}/{r.capacity}</span>
                  <span className="text-[#000000E6] font-semibold">{pct}%</span>
                </div>
                <Progress value={pct} className="h-1.5"/>
              </div>
            );
          }},
          { key: "live_class", label: "Live class", render: r => (
            r.live_class_url ? (
              <div className="flex items-center gap-2">
                <a href={r.live_class_url} target="_blank" rel="noreferrer" data-testid={`join-live-${r.id}`}
                  className="text-xs px-3 py-1 rounded-full bg-[#057642] text-white hover:bg-[#046238] font-semibold inline-flex items-center gap-1">
                  <Video size={12}/> Join <ExternalLink size={10}/>
                </a>
                <button onClick={()=>{ setLiveOpen(r); setUrlDraft(r.live_class_url || ""); }} data-testid={`edit-live-${r.id}`} className="text-xs text-[#0A66C2] hover:underline">Edit</button>
              </div>
            ) : (
              <button onClick={()=>{ setLiveOpen(r); setUrlDraft(""); }} data-testid={`set-live-${r.id}`}
                className="text-xs px-3 py-1 rounded-full bg-[#EDEBE7] hover:bg-[#E0DFDC] font-semibold inline-flex items-center gap-1">
                <Video size={12}/> Set URL
              </button>
            )
          )},
        ]}
        rows={batches}
      />

      <Dialog open={!!liveOpen} onOpenChange={(v)=>!v && setLiveOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Live class URL</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-[#00000099]">Paste your Zoom, Google Meet, or Microsoft Teams URL. Students will see a &quot;Join&quot; button on their timetable and here.</div>
            <input placeholder="https://us02web.zoom.us/j/..." data-testid="live-url-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={urlDraft} onChange={e=>setUrlDraft(e.target.value)}/>
            <DialogFooter><button onClick={saveLiveUrl} className="btn-primary" data-testid="save-live-url-btn">Save URL</button></DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
